/**
 * @author        patrick@zumper.com (Patrick Giddings)
 * @copyright     Copyright (c) 2024, Zumper
 */

import { formatInTimeZone } from 'date-fns-tz'
import { Bizo, autoclass } from './base'
import type {
  RawResidentQuote,
  RawResidentQuotes,
  ResidentQuote as ResidentQuoteType,
  ResidentQuotes as ResidentQuotesType,
} from './residentQuotes.types'

export const ResidentQuote: typeof ResidentQuoteType = autoclass<
  ResidentQuoteType,
  RawResidentQuote
>('ResidentQuote', class ResidentQuote extends Bizo<ResidentQuoteType, RawResidentQuote> {}, {
  category: 'category',
  illo: 'illo',
  isThumbnail: 'is_thumbnail',
  quote: 'quote',
  quotedOn: 'quoted_on',
  getQuotedOnText: (t: ResidentQuoteType) =>
    function getQuotedOnText(
      this: ResidentQuoteType,
      ...[tz]: Parameters<ResidentQuoteType['getQuotedOnText']>
    ): ReturnType<ResidentQuoteType['getQuotedOnText']> {
      return formatInTimeZone(
        new Date(this._data.quoted_on * 1000),
        tz ?? 'America/Chicago',
        'MMM yyyy'
      )
    }.bind(t),
})

export type ResidentQuote = ResidentQuoteType

export const ResidentQuotes: typeof ResidentQuotesType = autoclass<
  ResidentQuotesType,
  RawResidentQuotes
>('ResidentQuotes', class ResidentQuotes extends Bizo<ResidentQuotesType, RawResidentQuotes> {}, {
  propertySummary: 'property_summary',
  quotes: {
    isArray: true,
    name: 'quotes',
    type: ResidentQuote,
  },
  thumbnailQuote: (t: ResidentQuotesType) => {
    return (
      t.quotes.find((q) => q.isThumbnail) ?? t.quotes.find((q) => q.quote !== t.propertySummary)
    )
  },
  url: 'url',
})

export type ResidentQuotes = ResidentQuotesType
